/**
 * Synthetic actionLog entries: one row per distinct `entry.type` used by the battle
 * action history drawer / i18n, for developer proofreading and UI QA.
 *
 * @param {{ userId?: *, opponentId?: * }} ctx
 * @returns {object[]}
 */
export function buildMockActionHistorySample(ctx = {}) {
  const pid = ctx.userId != null ? ctx.userId : 'mock-player';
  const oid = ctx.opponentId != null ? ctx.opponentId : 'mock-opponent';

  const U = (id, name, instanceId) => ({ id, name, instanceId });
  const ua = U(501, 'Mock Attacker', 'mock-attacker');
  const ut = U(502, 'Mock Target', 'mock-target');
  const uw = U(503, 'Mock Ally', 'mock-ally');
  const weapon = U(601, 'Mock Rifle', 'mock-weapon');
  const drone = U(701, 'Mock Drone', 'mock-drone');
  const strat = { id: 801, name: 'Mock Strategem', instanceId: 'mock-strategem' };
  const spawned = U(901, 'Spawned Unit', 'mock-spawned');
  const summoned = U(902, 'Summoned Unit', 'mock-summoned');
  const killed = U(903, 'Redeployed Victim', 'mock-killed');

  const pushSeq = (arr, entry) => {
    const i = arr.length;
    arr.push({
      ...entry,
      sequence: i,
      timestamp: Date.now() + i
    });
  };

  const out = [];

  pushSeq(out, {
    type: 'turn-started',
    playerId: pid,
    turnNumber: 9,
    message: `Dev mock — turn started`
  });

  pushSeq(out, {
    type: 'unit-deployed',
    playerId: pid,
    zone: 'front',
    unit: ua,
    order: ua,
    message: `${ua.name} deployed to Front`
  });

  pushSeq(out, {
    type: 'unit-deployed',
    playerId: pid,
    zone: 'flank',
    unit: ut,
    order: ut,
    message: `${ut.name} deployed to Flank`
  });

  pushSeq(out, {
    type: 'weapon-equipped',
    playerId: pid,
    weapon,
    target: ut,
    message: `${ut.name} has equipped ${weapon.name}`
  });

  pushSeq(out, {
    type: 'weapon-stat-bonus',
    playerId: pid,
    weapon,
    target: ut,
    attackDelta: 2,
    healthDelta: 1,
    message: `${weapon.name} on ${ut.name}: +2 attack, +1 health`
  });

  pushSeq(out, {
    type: 'drone-deployed',
    playerId: pid,
    drone,
    unit: drone,
    order: drone,
    message: `${drone.name} has been deployed`
  });

  pushSeq(out, {
    type: 'drone-charge-used',
    playerId: pid,
    drone,
    unit: drone,
    order: drone,
    target: ut,
    message: `${drone.name} was used on ${ut.name}`
  });

  pushSeq(out, {
    type: 'drone-charge-used',
    playerId: pid,
    drone,
    unit: drone,
    order: drone,
    message: `${drone.name} was used`
  });

  pushSeq(out, {
    type: 'drone-aura-applied',
    playerId: pid,
    drone,
    attackDelta: 1,
    healthDelta: 2,
    scope: 'friendly_units',
    message: `${drone.name} aura (friendly_units): +1 attack, +2 health`
  });

  pushSeq(out, {
    type: 'drone-exhausted-deploy',
    playerId: pid,
    drone,
    spawnedUnit: spawned,
    zone: 'front',
    message: `${drone.name} exhausted — deployed ${spawned.name} to Front`
  });

  pushSeq(out, {
    type: 'unit-attacked',
    playerId: pid,
    attacker: { ...ua, playerId: pid },
    target: { ...ut, playerId: oid },
    amount: 2,
    damageToAttacker: 1,
    targetDestroyed: true,
    attackerDestroyed: false,
    outcome: {
      attackerHealthBefore: 2,
      attackerHealthAfter: 1,
      targetHealthBefore: 1,
      targetHealthAfter: 0,
      damageToTarget: 2,
      damageToAttacker: 1
    },
    affectedIntel: { playerId: pid, before: 6, after: 5 },
    message: `${ua.name} dealt 2 damage to ${ut.name}\n${ua.name} suffered 1 damage in return\n${ut.name} was destroyed`
  });

  pushSeq(out, {
    type: 'unit-attacked-general',
    playerId: pid,
    attacker: ua,
    amount: 3,
    damageToAttacker: 0,
    attackerDestroyed: false,
    targetPlayerId: oid,
    outcome: { targetPlayerId: oid },
    message: `${ua.name} dealt 3 damage to`
  });

  pushSeq(out, {
    type: 'unit-attacked-general',
    playerId: pid,
    attacker: ua,
    amount: 2,
    targetPlayerId: pid,
    outcome: { targetPlayerId: pid },
    message: `${ua.name} dealt 2 damage to`
  });

  pushSeq(out, {
    type: 'strategem-requested',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    target: ut,
    message: `${strat.name} was requested on ${ut.name}`
  });

  pushSeq(out, {
    type: 'strategem-requested',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    message: `${strat.name} was requested`
  });

  pushSeq(out, {
    type: 'strategem-damaged-unit',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    target: ut,
    amount: 5,
    message: `${strat.name} damaged ${ut.name} for 5`
  });

  pushSeq(out, {
    type: 'strategem-damaged-general',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    amount: 2,
    targetPlayerId: pid,
    message: `${strat.name} dealt 2 damage to your general`
  });

  pushSeq(out, {
    type: 'strategem-damaged-general',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    amount: 3,
    targetPlayerId: oid,
    message: `${strat.name} dealt 3 damage to enemy general`
  });

  pushSeq(out, {
    type: 'strategem-destroyed-unit',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    target: ut,
    message: `${strat.name} destroyed ${ut.name}`
  });

  pushSeq(out, {
    type: 'strategem-spawned-unit',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    spawnedUnit: spawned,
    message: `${strat.name} spawned ${spawned.name}`
  });

  pushSeq(out, {
    type: 'strategem-grew-unit',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    target: ut,
    message: `${strat.name} grew ${ut.name}`
  });

  pushSeq(out, {
    type: 'strategem-shrank-unit',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    target: ut,
    message: `${strat.name} shrank ${ut.name}`
  });

  pushSeq(out, {
    type: 'strategem-decommissioned-unit',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    target: ut,
    message: `${strat.name} decommissioned ${ut.name}`
  });

  pushSeq(out, {
    type: 'strategem-multi-target',
    playerId: pid,
    strategem: strat,
    order: strat,
    unit: strat,
    targets: [ut, uw],
    message: `${strat.name} affected 2 unit(s)`
  });

  pushSeq(out, {
    type: 'confuse',
    playerId: pid,
    unit: ua,
    order: ua,
    target: ut,
    message: `${ua.name} confused ${ut.name}`
  });

  pushSeq(out, {
    type: 'immobilize',
    playerId: pid,
    unit: ua,
    order: ua,
    target: ut,
    message: `${ua.name} immobilized ${ut.name}`
  });

  pushSeq(out, {
    type: 'deploy-effect',
    playerId: pid,
    unit: ua,
    order: ua,
    effectDescription: 'Custom deploy effect description (mock).',
    message: 'Custom deploy effect description (mock).'
  });

  pushSeq(out, {
    type: 'deploy-effect',
    playerId: pid,
    unit: { ...ua, id: 309 },
    order: { ...ua, id: 309 },
    target: ut,
    message: `${ua.name} shielded ${ut.name}`
  });

  pushSeq(out, {
    type: 'deploy-effect',
    playerId: pid,
    unit: ua,
    order: ua,
    target: ut,
    message: `${ua.name} targeted ${ut.name} with a deploy effect`
  });

  pushSeq(out, {
    type: 'deploy-effect',
    playerId: pid,
    unit: ua,
    order: ua,
    message: `${ua.name} deploy effect`
  });

  pushSeq(out, {
    type: 'unit-special-effect-damage',
    playerId: pid,
    unit: ua,
    order: ua,
    target: ut,
    amount: 7,
    message: `${ua.name} dealt 7 to ${ut.name}`
  });

  pushSeq(out, {
    type: 'drone-damage',
    playerId: pid,
    drone,
    unit: drone,
    order: drone,
    description: 'Drone dealt splash damage (mock).',
    message: 'Drone dealt splash damage (mock).'
  });

  pushSeq(out, {
    type: 'drone-non-damage',
    playerId: pid,
    drone,
    unit: drone,
    order: drone,
    effectType: 'disabled',
    target: ut,
    message: `${drone.name} disabled on ${ut.name}`
  });

  pushSeq(out, {
    type: 'eu-boss-weapons-randomizer-fired',
    playerId: pid,
    target: ut,
    targetUnitName: ut.name,
    message: `Boss weapons randomizer targeted ${ut.name}`
  });

  pushSeq(out, {
    type: 'eu-boss-weapons-randomizer-skipped',
    playerId: pid,
    message: 'Boss weapons randomizer skipped'
  });

  pushSeq(out, {
    type: 'penetrate-excess-general',
    playerId: pid,
    attacker: ua,
    target: ut,
    excessDamage: 6,
    generalPlayerId: oid,
    message: `${ua.name} dealt 6 excess Penetrate damage to general (via ${ut.name})`
  });

  pushSeq(out, {
    type: 'fell-intel',
    playerId: pid,
    attacker: ua,
    target: ut,
    intelGained: 2,
    message: `${ua.name} felled ${ut.name}, gained 2 permanent intel`
  });

  pushSeq(out, {
    type: 'fell-summon',
    playerId: pid,
    attacker: ua,
    summonedUnit: summoned,
    killedUnit: killed,
    message: `${ua.name} Fell redeployed ${killed.name} as ${summoned.name}`
  });

  pushSeq(out, {
    type: 'fell-summon',
    playerId: pid,
    attacker: ua,
    summonedUnit: summoned,
    message: `${ua.name} Fell summoned ${summoned.name}`
  });

  pushSeq(out, {
    type: 'unit-killed',
    playerId: pid,
    unit: ua,
    order: ua,
    target: ut,
    message: `${ua.name} destroyed ${ut.name}`
  });

  pushSeq(out, {
    type: 'turn-passed',
    playerId: pid,
    message: `Player ${pid} passed the turn`
  });

  return out;
}
